import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  Modal,
  ScrollView,
} from 'react-native';
import {
  createRevenueSplit,
  getChargerRevenueSplits,
  updateRevenueSplit,
  revokeRevenueSplit,
  copySplitsFromCharger,
  RevenueSplit,
} from '../../services/revenue-split-service';
import { isSuccessful } from '../../services/api-requests';
import { PayoutStatusIndicator } from '../RevenueSplit';
import { Ionicons } from '@expo/vector-icons';
import { InteractiveSplitManagementGuide } from '../Tutorials';

interface RevenueSplitManagerProps {
  chargerId: string | null;
  isEditing: boolean;
  baseRate?: number; // price_kwh_peak for effective rate calculation
  availableChargers?: Array<{ uuid: string; name: string }>; // For copy splits feature
}

interface SplitFormData {
  id: string;
  partnerEmail: string;
  splitPercentage: string;
  splitId?: string;
  isExisting: boolean;
  isModified: boolean;
  originalPercentage?: number;
  hasPayoutAccount?: boolean;
  payoutStatus?: 'ENABLED' | 'DISABLED';
}

const RevenueSplitManager: React.FC<RevenueSplitManagerProps> = ({
  chargerId,
  isEditing,
  baseRate,
  availableChargers = [],
}) => {
  const [splits, setSplits] = useState<SplitFormData[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyingFrom, setCopyingFrom] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);

  // Load existing splits when editing
  useEffect(() => {
    if (isEditing && chargerId) {
      loadExistingSplits();
    }
  }, [isEditing, chargerId]);

  const loadExistingSplits = async () => {
    if (!chargerId) return;

    setLoading(true);
    try {
      const response = await getChargerRevenueSplits(chargerId, 'ACTIVE');
      if (isSuccessful(response.status) && response.payload) {
        const existingSplits: SplitFormData[] = response.payload.chargerSplits.map(
          (split) => ({
            id: split.splitId,
            partnerEmail: split.partner?.email || split.partner?.username || '',
            splitPercentage: split.splitPercentage.toString(),
            splitId: split.splitId,
            isExisting: true,
            isModified: false,
            originalPercentage: split.splitPercentage,
            hasPayoutAccount: split.partner?.hasPayoutAccount,
            payoutStatus: split.partner?.payoutStatus,
          })
        );
        setSplits(existingSplits);
      } else {
        console.error('Failed to load revenue splits:', response.payload);
      }
    } catch (error) {
      console.error('Error loading revenue splits:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNewSplit = () => {
    const newSplit: SplitFormData = {
      id: Date.now().toString(),
      partnerEmail: '',
      splitPercentage: '',
      isExisting: false,
      isModified: false,
    };
    setSplits([...splits, newSplit]);
  };

  const updateSplitField = (
    id: string,
    field: 'partnerEmail' | 'splitPercentage',
    value: string
  ) => {
    setSplits(
      splits.map((split) => {
        if (split.id === id) {
          const isModified =
            split.isExisting &&
            (field === 'splitPercentage'
              ? parseFloat(value) !== split.originalPercentage
              : false);
          return { ...split, [field]: value, isModified };
        }
        return split;
      })
    );
    // Clear error for this field
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${id}_${field}`];
      return newErrors;
    });
  };

  const validateSplit = (split: SplitFormData): boolean => {
    const newErrors: Record<string, string> = {};
    
    // Validate email
    if (!split.partnerEmail.trim()) {
      newErrors[`${split.id}_partnerEmail`] = 'Partner email is required';
    } else if (!split.partnerEmail.includes('@') && !split.partnerEmail.includes('.')) {
      newErrors[`${split.id}_partnerEmail`] = 'Enter a valid email or username';
    }

    // Validate percentage
    const percentage = parseFloat(split.splitPercentage);
    if (!split.splitPercentage.trim()) {
      newErrors[`${split.id}_splitPercentage`] = 'Percentage is required';
    } else if (isNaN(percentage)) {
      newErrors[`${split.id}_splitPercentage`] = 'Enter a valid number';
    } else if (percentage < 0.01 || percentage > 100) {
      newErrors[`${split.id}_splitPercentage`] = 'Must be between 0.01 and 100';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const saveSplit = async (index: number) => {
    const split = splits[index];
    
    if (!validateSplit(split)) {
      return;
    }

    if (!chargerId) {
      if (Platform.OS === 'web') {
        alert('Please save the charger first before adding revenue splits.');
      } else {
        Alert.alert('Error', 'Please save the charger first before adding revenue splits.');
      }
      return;
    }

    setSavingIndex(index);
    try {
      if (split.isExisting && split.isModified && split.splitId) {
        // Update existing split
        const response = await updateRevenueSplit(chargerId, split.splitId, {
          splitPercentage: parseFloat(split.splitPercentage),
        });

        if (isSuccessful(response.status)) {
          // Update local state to reflect saved changes
          setSplits(
            splits.map((s, i) =>
              i === index
                ? {
                    ...s,
                    isModified: false,
                    originalPercentage: parseFloat(split.splitPercentage),
                  }
                : s
            )
          );
          if (Platform.OS === 'web') {
            alert('Revenue split updated successfully!');
          } else {
            Alert.alert('Success', 'Revenue split updated successfully!');
          }
        } else {
          // Extract error message from response
          const errorMessage = (response.payload as any)?.message || 'Failed to update revenue split';
          throw new Error(errorMessage);
        }
      } else if (!split.isExisting) {
        // Create new split
        const response = await createRevenueSplit(
          chargerId,
          split.partnerEmail,
          parseFloat(split.splitPercentage)
        );

        if (isSuccessful(response.status) && response.payload) {
          // Update local state with the created split
          setSplits(
            splits.map((s, i) =>
              i === index
                ? {
                    ...s,
                    splitId: response.payload!.revenueSplit.splitId,
                    isExisting: true,
                    isModified: false,
                    originalPercentage: parseFloat(split.splitPercentage),
                  }
                : s
            )
          );
          if (Platform.OS === 'web') {
            alert('Revenue split created successfully!');
          } else {
            Alert.alert('Success', 'Revenue split created successfully!');
          }
        } else {
          // Extract error message from response
          const errorMessage = (response.payload as any)?.message || 'Failed to create revenue split';
          throw new Error(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error saving split:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save revenue split. Please try again.';
      if (Platform.OS === 'web') {
        alert(errorMessage);
      } else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setSavingIndex(null);
    }
  };

  const confirmRemoveSplit = (index: number) => {
    const split = splits[index];
    
    if (split.isExisting && split.splitId) {
      // Show confirmation for existing splits
      if (Platform.OS === 'web') {
        if (window.confirm(
          `Are you sure you want to remove the revenue split for ${split.partnerEmail}? ` +
          'Future earnings will not be split with this partner.'
        )) {
          removeSplit(index);
        }
      } else {
        Alert.alert(
          'Confirm Removal',
          `Are you sure you want to remove the revenue split for ${split.partnerEmail}? ` +
          'Future earnings will not be split with this partner.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Remove', onPress: () => removeSplit(index), style: 'destructive' },
          ]
        );
      }
    } else {
      // Just remove unsaved splits without confirmation
      removeSplit(index);
    }
  };

  const removeSplit = async (index: number) => {
    const split = splits[index];

    if (split.isExisting && split.splitId && chargerId) {
      setSavingIndex(index);
      try {
        const response = await revokeRevenueSplit(chargerId, split.splitId);
        if (isSuccessful(response.status)) {
          setSplits(splits.filter((_, i) => i !== index));
          if (Platform.OS === 'web') {
            alert('Revenue split removed successfully!');
          } else {
            Alert.alert('Success', 'Revenue split removed successfully!');
          }
        } else {
          // Extract error message from response
          const errorMessage = (response.payload as any)?.message || 'Failed to revoke revenue split';
          throw new Error(errorMessage);
        }
      } catch (error) {
        console.error('Error removing split:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to remove revenue split. Please try again.';
        if (Platform.OS === 'web') {
          alert(errorMessage);
        } else {
          Alert.alert('Error', errorMessage);
        }
      } finally {
        setSavingIndex(null);
      }
    } else {
      // Just remove from local state
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  const getTotalPercentage = () => {
    return splits.reduce((total, split) => {
      const percentage = parseFloat(split.splitPercentage);
      return total + (isNaN(percentage) ? 0 : percentage);
    }, 0);
  };

  const getEffectiveRate = () => {
    if (!baseRate) return null;
    const totalSplit = getTotalPercentage();
    return baseRate * (1 - totalSplit / 100);
  };

  const handleCopySplits = async (sourceChargerId: string) => {
    if (!chargerId) return;

    setCopyingFrom(sourceChargerId);
    try {
      const response = await copySplitsFromCharger(chargerId, sourceChargerId);
      if (isSuccessful(response.status) && response.payload) {
        if (Platform.OS === 'web') {
          alert(`Successfully copied ${response.payload.copiedCount} split(s)`);
        } else {
          Alert.alert('Success', `Successfully copied ${response.payload.copiedCount} split(s)`);
        }
        // Reload splits
        await loadExistingSplits();
        setShowCopyModal(false);
      } else {
        throw new Error((response.payload as any)?.message || 'Failed to copy splits');
      }
    } catch (error) {
      console.error('Error copying splits:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to copy splits';
      if (Platform.OS === 'web') {
        alert(errorMessage);
      } else {
        Alert.alert('Error', errorMessage);
      }
    } finally {
      setCopyingFrom(null);
    }
  };

  const totalPercentage = getTotalPercentage();
  const exceeds100 = totalPercentage > 100;
  const effectiveRate = getEffectiveRate();

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color="#51B868" />
        <Text style={styles.loadingText}>Loading revenue splits...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <View style={{flex: 1}}>
          <Text style={styles.sectionTitle}>Designate Revenue Partner(s)</Text>
          <Text style={styles.description}>
            Share a percentage of your charger revenue with partners. Total splits cannot exceed 100%.
          </Text>
          {effectiveRate !== null && baseRate && (
            <View style={styles.effectiveRateContainer}>
              <Ionicons name="calculator-outline" size={16} color="#3B82F6" />
              <Text style={styles.effectiveRateText}>
                Your effective rate: <Text style={styles.effectiveRateValue}>${effectiveRate.toFixed(4)}/kWh</Text>
                {totalPercentage > 0 && <Text style={styles.effectiveRateDetail}> (after {totalPercentage.toFixed(2)}% splits)</Text>}
              </Text>
            </View>
          )}
        </View>
        
        {/* Help and Copy buttons */}
        <View style={styles.headerButtons}>
          <TouchableOpacity
            style={styles.helpButton}
            onPress={() => setShowGuide(true)}
          >
            <Ionicons name="help-circle-outline" size={24} color="#3B82F6" />
          </TouchableOpacity>
          
          {isEditing && availableChargers.length > 0 && (
            <TouchableOpacity
              style={styles.copyButton}
              onPress={() => setShowCopyModal(true)}
            >
              <Ionicons name="copy-outline" size={16} color="#3B82F6" />
              <Text style={styles.copyButtonText}>Copy</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      {splits.map((split, index) => (
        <View key={split.id} style={styles.splitRow}>
          <View style={styles.inputsContainer}>
            <View style={styles.inputWrapper}>
              <Text style={styles.inputLabel}>Partner Email</Text>
              <TextInput
                style={[
                  styles.input,
                  styles.emailInput,
                  errors[`${split.id}_partnerEmail`] && styles.inputError,
                ]}
                placeholder="partner@example.com"
                placeholderTextColor="#8D8E90"
                value={split.partnerEmail}
                onChangeText={(text) => updateSplitField(split.id, 'partnerEmail', text)}
                editable={!split.isExisting}
                autoComplete="off"
                autoCorrect={false}
                keyboardType="email-address"
              />
              {errors[`${split.id}_partnerEmail`] && (
                <Text style={styles.errorText}>{errors[`${split.id}_partnerEmail`]}</Text>
              )}
              {split.isExisting && split.hasPayoutAccount !== undefined && (
                <View style={{ marginTop: 4 }}>
                  <PayoutStatusIndicator
                    hasPayoutAccount={split.hasPayoutAccount}
                    payoutStatus={split.payoutStatus}
                  />
                </View>
              )}
            </View>

            <View style={styles.inputWrapper}>
              <Text style={styles.inputLabel}>% Split</Text>
              <TextInput
                style={[
                  styles.input,
                  styles.percentageInput,
                  errors[`${split.id}_splitPercentage`] && styles.inputError,
                ]}
                placeholder="25.00"
                placeholderTextColor="#8D8E90"
                value={split.splitPercentage}
                onChangeText={(text) => updateSplitField(split.id, 'splitPercentage', text)}
                keyboardType="decimal-pad"
                autoComplete="off"
                autoCorrect={false}
              />
              {errors[`${split.id}_splitPercentage`] && (
                <Text style={styles.errorText}>{errors[`${split.id}_splitPercentage`]}</Text>
              )}
            </View>
          </View>

          <View style={styles.actionsContainer}>
            {split.isModified && (
              <TouchableOpacity
                style={styles.saveButton}
                onPress={() => saveSplit(index)}
                disabled={savingIndex === index}
              >
                {savingIndex === index ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </TouchableOpacity>
            )}
            {!split.isExisting && (
              <TouchableOpacity
                style={styles.saveButton}
                onPress={() => saveSplit(index)}
                disabled={savingIndex === index || !chargerId}
              >
                {savingIndex === index ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </TouchableOpacity>
            )}
            <TouchableOpacity
              style={styles.removeButton}
              onPress={() => confirmRemoveSplit(index)}
              disabled={savingIndex === index}
            >
              <Text style={styles.removeButtonText}>−</Text>
            </TouchableOpacity>
          </View>
        </View>
      ))}

      <TouchableOpacity style={styles.addButton} onPress={addNewSplit}>
        <Text style={styles.addButtonText}>+ Add Partner</Text>
      </TouchableOpacity>

      {splits.length > 0 && (
        <View style={styles.totalContainer}>
          <Text style={[styles.totalText, exceeds100 && styles.totalError]}>
            Total Split: {totalPercentage.toFixed(2)}%
          </Text>
          {exceeds100 && (
            <Text style={styles.errorText}>Total splits cannot exceed 100%</Text>
          )}
        </View>
      )}

      {/* Copy Splits Modal */}
      <Modal
        visible={showCopyModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowCopyModal(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowCopyModal(false)}
        >
          <TouchableOpacity
            style={styles.modalContent}
            activeOpacity={1}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Copy Splits From Charger</Text>
              <TouchableOpacity onPress={() => setShowCopyModal(false)}>
                <Ionicons name="close" size={24} color="#8D8E90" />
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.chargerList}>
              {availableChargers
                .filter(c => c.uuid !== chargerId)
                .map((charger) => (
                  <TouchableOpacity
                    key={charger.uuid}
                    style={styles.chargerItem}
                    onPress={() => handleCopySplits(charger.uuid)}
                    disabled={copyingFrom === charger.uuid}
                  >
                    <Text style={styles.chargerName}>{charger.name}</Text>
                    {copyingFrom === charger.uuid && (
                      <ActivityIndicator size="small" color="#3B82F6" />
                    )}
                  </TouchableOpacity>
                ))}
            </ScrollView>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Interactive Split Management Guide */}
      <InteractiveSplitManagementGuide
        visible={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 24,
    marginBottom: 16,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginTop: 24,
  },
  loadingText: {
    color: '#fff',
    marginLeft: 12,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  description: {
    color: '#8D8E90',
    fontSize: 14,
    marginBottom: 8,
    lineHeight: 20,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  effectiveRateContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    padding: 8,
    borderRadius: 8,
    marginTop: 8,
  },
  effectiveRateText: {
    color: '#8D8E90',
    fontSize: 13,
    flex: 1,
  },
  effectiveRateValue: {
    color: '#3B82F6',
    fontWeight: '700',
  },
  effectiveRateDetail: {
    color: '#8D8E90',
    fontSize: 12,
  },
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  helpButton: {
    padding: 4,
  },
  copyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  copyButtonText: {
    color: '#3B82F6',
    fontSize: 14,
    fontWeight: '600',
  },
  splitRow: {
    marginBottom: 16,
    backgroundColor: '#1A1D23',
    padding: 12,
    borderRadius: 12,
  },
  inputsContainer: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 8,
  },
  inputWrapper: {
    flex: 1,
  },
  inputLabel: {
    color: '#8D8E90',
    fontSize: 12,
    marginBottom: 4,
    fontWeight: '600',
  },
  input: {
    backgroundColor: '#292D33',
    color: '#fff',
    padding: 10,
    borderRadius: 8,
    fontSize: 14,
  },
  emailInput: {
    flex: 2,
  },
  percentageInput: {
    flex: 1,
  },
  inputError: {
    borderWidth: 1,
    borderColor: '#E74C3C',
  },
  errorText: {
    color: '#E74C3C',
    fontSize: 12,
    marginTop: 4,
  },
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#51B868',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    minWidth: 60,
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  removeButton: {
    backgroundColor: '#E74C3C',
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
  },
  removeButtonText: {
    color: '#fff',
    fontSize: 24,
    fontWeight: '600',
    lineHeight: 24,
  },
  addButton: {
    backgroundColor: '#292D33',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#51B868',
    borderStyle: 'dashed',
  },
  addButtonText: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '600',
  },
  totalContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#1A1D23',
    borderRadius: 8,
  },
  totalText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'right',
  },
  totalError: {
    color: '#E74C3C',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#1A1D23',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#292D33',
  },
  modalTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
  },
  chargerList: {
    maxHeight: 400,
    padding: 16,
  },
  chargerItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#292D33',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
  },
  chargerName: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default RevenueSplitManager;

